import React from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import * as Inovx from '@shared';
import KnowledgeBookActions from '../../actions/KnowledgeBookActions';
import KnowledgeBookStore from '../../stores/KnowledgeBookStore';
import ClassificationTags from './ClassificationTags';

const InovxControls = Inovx.Shared.Controls;

interface AssignClassificationTagsState {
    isModalOpen: boolean;
    selectedTagIds: string[];
}

function getStoreState(): AssignClassificationTagsState {
    return {
        isModalOpen: KnowledgeBookStore.getAssignClassificationTagsModalVisible(),
        selectedTagIds: KnowledgeBookStore.getAssignClassificationTagsSelectedTagIds(),
    };
}

const KnowledgeBookAssignClassificationTagsForm: React.FC = () => {
    const [state, setState] = React.useState<AssignClassificationTagsState>(getStoreState);

    React.useEffect(() => {
        const handleStoreChange = () => {
            setState(getStoreState());
        };

        KnowledgeBookStore.addChangeListener(handleStoreChange);
        return () => {
            KnowledgeBookStore.removeChangeListener(handleStoreChange);
        };
    }, []);

    const closeDialog = React.useCallback(() => {
        KnowledgeBookActions.closeAssignClassificationTagsForm();
    }, []);

    const handleBeforeClose = React.useCallback(() => {
        KnowledgeBookActions.closeAssignClassificationTagsForm();
    }, []);

    const handleSelectedTagsChange = React.useCallback((selectedTagIds: string[]) => {
        KnowledgeBookActions.updateAssignClassificationTagsSelection(selectedTagIds);
    }, []);

    const addTags = React.useCallback(async () => {
        try {
            await KnowledgeBookActions.assignClassificationTagsToKnowledgeBooks(false);
        } catch {
            // Shared service error handling reports the failure; keep the dialog open.
        }
    }, []);

    const replaceTags = React.useCallback(async () => {
        try {
            await KnowledgeBookActions.assignClassificationTagsToKnowledgeBooks(true);
        } catch {
            // Shared service error handling reports the failure; keep the dialog open.
        }
    }, []);

    const renderHeader = () => (
        <div>
            <div className="e-icon-settings dlg-template">
                {InovxLoader.t('facility:knowledgeBook.assignClassificationTagsTitle')}
            </div>
        </div>
    );

    const renderFooter = () => (
        <div>
            <div className="d-flex j-cc">
                <div>
                    <a
                        id="facilityApp-assign-tags-add-btn"
                        onClick={state.selectedTagIds.length > 0 ? addTags : undefined}
                        className={state.selectedTagIds.length > 0 ? 'btn btn-primary' : 'btn btn-primary disabled'}
                        aria-disabled={state.selectedTagIds.length === 0}
                    >
                        {InovxLoader.t('facility:knowledgeBook.addTags')}
                    </a>
                </div>
                <div className="ml-1">
                    <a
                        id="facilityApp-assign-tags-replace-btn"
                        onClick={state.selectedTagIds.length > 0 ? replaceTags : undefined}
                        className={state.selectedTagIds.length > 0 ? 'btn btn-primary' : 'btn btn-primary disabled'}
                        aria-disabled={state.selectedTagIds.length === 0}
                    >
                        {InovxLoader.t('facility:knowledgeBook.replaceTags')}
                    </a>
                </div>
                <div className="ml-1">
                    <a
                        id="facilityApp-assign-tags-cancel-btn"
                        onClick={closeDialog}
                        className="btn btn-default"
                    >
                        {InovxLoader.t('facility:preferences.cancelButton')}
                    </a>
                </div>
            </div>
        </div>
    );

    if (!state.isModalOpen) {
        return null;
    }

    return (
        <DialogComponent
            target={'#app'}
            width='550px'
            header={renderHeader}
            footerTemplate={renderFooter}
            showCloseIcon={true}
            visible={state.isModalOpen}
            beforeClose={handleBeforeClose}
            isModal={true}
            zIndex={110000}
        >
            <div className="dialogContent">
                <div className="taxonomy-wrapper">
                    <InovxControls.FormField
                        title={InovxLoader.t('facility:knowledgeBook.classificationTags')}
                        inputName='ClassificationTags'
                    >
                        <ClassificationTags
                            selectedTags={state.selectedTagIds}
                            onSelectedTagsChange={handleSelectedTagsChange}
                            restrictToCurrentUserPermissions={true}
                        />
                    </InovxControls.FormField>
                </div>
            </div>
        </DialogComponent>
    );
};

export default KnowledgeBookAssignClassificationTagsForm;
